import { CurrentUserRoleLike, isAdminUser } from './userRoles';

export type PortfolioInterestMode = 'off' | 'include' | 'exclude' | 'custom';
export type PortfolioInterestAction = 'include' | 'exclude';
export type PortfolioInterestKey =
  | 'marketers'
  | 'manufacturers'
  | 'brands'
  | 'ingredients'
  | 'flavours'
  | 'fragrances'
  | 'packaging_types'
  | 'packaging_materials';
export type IngredientPortfolioFilterKey = 'ingredients' | 'ingredient_groups';
export type PortfolioPolicyInterestKey = PortfolioInterestKey | 'ingredient_groups';
export type PortfolioModuleKey = 'category_of_interest' | 'claim_of_interest';
export type PortfolioFilterKey = PortfolioPolicyInterestKey | PortfolioModuleKey;

export type PortfolioFilterRule = {
  enabled: boolean;
  allowed_modes: string[];
  default_mode: string;
  /** @deprecated Customize is inclusion-only; retained for API compatibility. */
  custom_actions?: PortfolioInterestAction[];
};

export type PortfolioFilterPolicy = {
  version?: number;
  active_ingredient_filter?: IngredientPortfolioFilterKey | null;
  filters?: Partial<Record<PortfolioFilterKey, PortfolioFilterRule>>;
};

export type PortfolioFilterCapability = {
  available: boolean;
  enabled: boolean;
  mandatory: boolean;
  reason: string;
  feature_key?: string;
  rule: PortfolioFilterRule;
};

export type PortfolioFilterCapabilities = {
  version?: number;
  active_ingredient_filter?: IngredientPortfolioFilterKey | null;
  policy?: PortfolioFilterPolicy;
  filters?: Partial<Record<PortfolioFilterKey, PortfolioFilterCapability>>;
};

type SelectOption = {
  value: PortfolioInterestMode;
  label: string;
};

const PORTFOLIO_INTEREST_OPTIONS: SelectOption[] = [
  { value: 'off', label: 'Off' },
  { value: 'include', label: 'Include all' },
  { value: 'exclude', label: 'Exclude all' },
  { value: 'custom', label: 'Customize' },
];

const DEFAULT_RULES: Record<PortfolioFilterKey, PortfolioFilterRule> = {
  marketers: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  manufacturers: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  brands: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  ingredients: {
    enabled: true,
    allowed_modes: ['include', 'custom'],
    default_mode: 'include',
    custom_actions: ['include'],
  },
  ingredient_groups: {
    enabled: true,
    allowed_modes: ['include', 'custom'],
    default_mode: 'include',
    custom_actions: ['include'],
  },
  flavours: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  fragrances: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  packaging_types: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  packaging_materials: {
    enabled: true,
    allowed_modes: ['off', 'include', 'exclude', 'custom'],
    default_mode: 'off',
    custom_actions: ['include'],
  },
  category_of_interest: {
    enabled: true,
    allowed_modes: ['all', 'custom', 'full_custom'],
    default_mode: 'all',
  },
  claim_of_interest: {
    enabled: true,
    allowed_modes: ['off', 'all', 'custom'],
    default_mode: 'off',
  },
};

const uniqueStrings = (value: unknown) =>
  Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === 'string'))]
    : [];

export const isPortfolioAdminUser = (currentUser: CurrentUserRoleLike) => isAdminUser(currentUser);

export const getActiveIngredientPortfolioFilterKey = (value?: {
  ingredient_interest_mode?: unknown;
  feature_flags?: Record<string, boolean>;
  portfolio_filter_policy?: PortfolioFilterPolicy;
  portfolio_filter_capabilities?: PortfolioFilterCapabilities;
} | null): IngredientPortfolioFilterKey =>
  value?.portfolio_filter_capabilities?.active_ingredient_filter ||
  value?.portfolio_filter_policy?.active_ingredient_filter ||
  (value?.ingredient_interest_mode === 'grouped' ||
  value?.feature_flags?.grouped_ingredient_interests === true
    ? 'ingredient_groups'
    : 'ingredients');

export const getPortfolioPolicyFilterKey = (
  key: PortfolioFilterKey,
  policy?: PortfolioFilterPolicy,
): PortfolioFilterKey =>
  key === 'ingredients' && policy?.active_ingredient_filter === 'ingredient_groups'
    ? 'ingredient_groups'
    : key;

export const normalizePortfolioInterestRequestForApi = (params: {
  currentUser?: {
    ingredient_interest_mode?: unknown;
    feature_flags?: Record<string, boolean>;
    portfolio_filter_policy?: PortfolioFilterPolicy;
    portfolio_filter_capabilities?: PortfolioFilterCapabilities;
  } | null;
  interestToggles?: Record<string, boolean>;
  interestActions?: Record<string, string>;
  interestSelections?: Partial<Record<string, number[]>>;
}) => {
  const activeIngredientKey = getActiveIngredientPortfolioFilterKey(
    params.currentUser,
  );
  const interestToggles = { ...(params.interestToggles || {}) };
  const interestActions = Object.entries(params.interestActions || {}).reduce<
    Record<string, PortfolioInterestAction>
  >((actions, [key, action]) => {
    actions[key] = action === 'exclude' ? 'exclude' : 'include';
    return actions;
  }, {});
  const interestSelections = Object.entries(params.interestSelections || {}).reduce<
    Record<string, number[]>
  >((selections, [key, ids]) => {
    if (Array.isArray(ids)) selections[key] = ids;
    return selections;
  }, {});

  if (activeIngredientKey === 'ingredient_groups') {
    if (interestToggles.ingredient_groups === undefined) {
      interestToggles.ingredient_groups = interestToggles.ingredients === true;
    }
    if (interestActions.ingredient_groups === undefined) {
      interestActions.ingredient_groups = interestActions.ingredients || 'include';
    }
    if (
      interestSelections.ingredient_groups === undefined &&
      Array.isArray(interestSelections.ingredients)
    ) {
      interestSelections.ingredient_groups = interestSelections.ingredients;
    }
    delete interestToggles.ingredients;
    delete interestActions.ingredients;
    delete interestSelections.ingredients;
  } else {
    delete interestToggles.ingredient_groups;
    delete interestActions.ingredient_groups;
    delete interestSelections.ingredient_groups;
  }

  return { interestToggles, interestActions, interestSelections };
};

export const getPortfolioFilterRule = (params: {
  key: PortfolioFilterKey;
  isAdmin: boolean;
  itemCount?: number;
  policy?: PortfolioFilterPolicy;
}): PortfolioFilterRule => {
  const { itemCount = 1, policy } = params;
  const key = getPortfolioPolicyFilterKey(params.key, policy);
  const defaultRule = DEFAULT_RULES[key];
  const base =
    params.isAdmin && (key === 'ingredients' || key === 'ingredient_groups')
      ? {
          enabled: true,
          allowed_modes: ['off', 'include', 'exclude', 'custom'],
          default_mode: 'include',
          custom_actions: ['include'] as PortfolioInterestAction[],
        }
      : defaultRule;
  const configured =
    policy?.filters?.[key] ||
    (key === 'ingredient_groups' ? policy?.filters?.ingredients : undefined);
  if (configured) {
    if (
      (key === 'ingredients' || key === 'ingredient_groups') &&
      itemCount <= 0 &&
      configured.enabled !== false
    ) {
      return {
        enabled: true,
        allowed_modes: ['off'],
        default_mode: 'off',
      };
    }
    return {
      enabled: configured.enabled !== false,
      allowed_modes: uniqueStrings(configured.allowed_modes),
      default_mode: configured.default_mode,
      ...(configured.allowed_modes.includes('custom')
        ? { custom_actions: ['include'] as PortfolioInterestAction[] }
        : {}),
    };
  }
  if ((key === 'ingredients' || key === 'ingredient_groups') && itemCount <= 0) {
    return {
      enabled: true,
      allowed_modes: ['off'],
      default_mode: 'off',
    };
  }

  return {
    enabled: base.enabled !== false,
    allowed_modes: uniqueStrings(base.allowed_modes),
    default_mode: base.default_mode,
    ...(base.custom_actions
      ? { custom_actions: [...base.custom_actions] }
      : {}),
  };
};

export const canUsePortfolioInterestOff = (params: {
  key?: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  policy?: PortfolioFilterPolicy;
}) => {
  const rule = getPortfolioFilterRule({
    key: params.key || 'marketers',
    isAdmin: params.isAdmin,
    itemCount: params.itemCount,
    policy: params.policy,
  });
  return rule.enabled && rule.allowed_modes.includes('off');
};

export const canUsePortfolioInterestExclude = (params: {
  key?: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  policy?: PortfolioFilterPolicy;
}) => {
  const rule = getPortfolioFilterRule({
    key: params.key || 'marketers',
    isAdmin: params.isAdmin,
    itemCount: params.itemCount,
    policy: params.policy,
  });
  return rule.enabled && rule.allowed_modes.includes('exclude');
};

export const getPortfolioCustomizationSelectedIds = (params: {
  allIds: number[];
  selectedIds?: number[];
  action?: PortfolioInterestAction;
}) => {
  const allIds = [...new Set(params.allIds.map(Number))].filter(
    (id) => Number.isInteger(id) && id > 0,
  );
  if (!Array.isArray(params.selectedIds)) return allIds;

  const allIdSet = new Set(allIds);
  const validSelectedIds = [
    ...new Set(params.selectedIds.map(Number)),
  ].filter((id) => Number.isInteger(id) && id > 0 && allIdSet.has(id));
  if (params.action !== 'exclude') return validSelectedIds;

  const legacyExcludedIds = new Set(validSelectedIds);
  return allIds.filter((id) => !legacyExcludedIds.has(id));
};

export const getPortfolioInterestOptions = (params: {
  key?: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  policy?: PortfolioFilterPolicy;
}) => {
  const rule = getPortfolioFilterRule({
    key: params.key || 'marketers',
    isAdmin: params.isAdmin,
    itemCount: params.itemCount,
    policy: params.policy,
  });
  if (!rule.enabled) return [];
  return PORTFOLIO_INTEREST_OPTIONS.filter((option) =>
    rule.allowed_modes.includes(option.value),
  );
};

export const getPortfolioInterestMode = (params: {
  key?: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  enabled: boolean;
  selectedIds?: number[];
  action?: PortfolioInterestAction;
  policy?: PortfolioFilterPolicy;
}): PortfolioInterestMode => {
  const key = params.key || 'marketers';
  const rule = getPortfolioFilterRule({ ...params, key });
  if (!rule.enabled) return 'off';
  if (!params.enabled) {
    return rule.allowed_modes.includes('off')
      ? 'off'
      : (rule.default_mode as PortfolioInterestMode);
  }

  if (
    Array.isArray(params.selectedIds) &&
    params.selectedIds.length > 0 &&
    rule.allowed_modes.includes('custom')
  ) {
    return 'custom';
  }

  if (params.action === 'exclude' && rule.allowed_modes.includes('exclude')) {
    return 'exclude';
  }
  return rule.allowed_modes.includes('include')
    ? 'include'
    : (rule.default_mode as PortfolioInterestMode);
};

export const shouldForcePortfolioInterestInclude = (params: {
  key?: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  enabled: boolean;
  policy?: PortfolioFilterPolicy;
}) => {
  const key = params.key || 'marketers';
  const rule = getPortfolioFilterRule({ ...params, key });
  return (
    rule.enabled &&
    !params.enabled &&
    params.itemCount > 0 &&
    !rule.allowed_modes.includes('off') &&
    rule.default_mode === 'include'
  );
};

export const getPortfolioInterestDefault = (params: {
  key: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  policy?: PortfolioFilterPolicy;
}): { enabled: boolean; action: PortfolioInterestAction } => {
  const rule = getPortfolioFilterRule(params);
  return {
    enabled: rule.enabled && rule.default_mode !== 'off',
    action: rule.default_mode === 'exclude' ? 'exclude' : 'include',
  };
};

export const getRequiredPortfolioInterestState = (params: {
  key: PortfolioInterestKey;
  isAdmin: boolean;
  itemCount: number;
  enabled: boolean;
  policy?: PortfolioFilterPolicy;
}): { enabled: true; action: PortfolioInterestAction } | null => {
  const rule = getPortfolioFilterRule(params);
  if (!rule.enabled || params.enabled || params.itemCount <= 0) return null;
  if (rule.allowed_modes.includes('off') || rule.default_mode === 'off') return null;
  return {
    enabled: true,
    action: rule.default_mode === 'exclude' ? 'exclude' : 'include',
  };
};
